﻿import { useState } from 'react';
import { Box, Stack, Button, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import SecureFileUploaderDataGrid from './Components/SecureFileUploaderDataGrid';
import SecureFileUploaderModal from './Components/SecureFileUploaderModal';
import { StrikeThroughHeader } from '../../components/Headers/strikethrough-header/strikethrough-header';
import { useUser } from '../../context/UserContext';
import {
  GetAgentOffices,
  GetFilesUploadedByAgencyAndStatus,
  GetFilesUploadedByProducer,
  GetFilterOptions_DropDown,
  GetAgentLineOfBusiness,
} from '../../api/SecureFileUploader';
import QuerySelect from '../../components/common/QuerySelect';
import ProducerDropdown from '../Software/Subpages/MyIssuedApps/components/ProducerDropdown';

function Index() {
  const user = useUser();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [producerID, _setProducerID] = useState(user?.producerID);
  const [agentInfo, _setAgentInfo] = useState({
    agencyName: user?.agency,
    emailAddress: user?.emailAddress,
    displayName: user?.displayName,
    npnID: user?.npnID,
  });

  const [filterData, setFilterData] = useState({
    ProducerID: user?.producerID,
    Agency: '',
    AgencyID: '',
    IsRSA: user.permissions.RSA,
    IsManager: user.permissions.AgencyManagement,
    TeamCode: '',
    FileStatus: '0',
    AgentFirstName: '',
    AgentLastName: '',
    AgentType: '',
  });

  const isIMO = user.userType === 'IMO' || user.userType === 'SUB-IMO';

  const { data: producerLOB } = useQuery({
    queryKey: ['getAgentLineOfBusiness', filterData.ProducerID],
    queryFn: GetAgentLineOfBusiness(filterData?.ProducerID),
    enabled: !!(isIMO && filterData.ProducerID),
  });

  // permissions setup for conditional rendering
  const hasMYGA = isIMO
    ? producerLOB?.data?.some((product) => product.toUpperCase() === 'MYGA')
    : user.LOB.some((product) => product.ProductCode.toUpperCase() === 'MYGA');

  const canUseEApp = isIMO
    ? producerLOB?.data?.some((product) => ['GENLEG', 'SPWL'].includes(product.toUpperCase()))
    : user.LOB.some((product) => ['GENLEG', 'SPWL'].includes(product.ProductCode.toUpperCase()));

  const userLOB = isIMO ? producerLOB?.data : user.LOB.map((LOB) => LOB.ProductCode);

  const isCASG = user.division === 'CASG';
  const isRSA = user.permissions.RSA === true;
  const isManager = user.permissions.AgencyManagement;
  const isAdmin = (isRSA && !isIMO) || (isManager && !isIMO);

  const permissions = {
    hasMYGA,
    canUseEApp,
    isCASG,
    isRSA,
    isManager,
    isIMO,
    userLOB,
    isAdmin,
  };

  const getFilesUploadedByAgencyAndStatus = useQuery({
    queryKey: [
      'GetFilesUploadedByAgencyAndStatus',
      filterData?.Agency,
      filterData?.FileStatus,
      filterData?.ProducerID,
    ],
    queryFn: GetFilesUploadedByAgencyAndStatus(filterData),
    enabled: !!(
      user !== null &&
      filterData?.Agency !== '' &&
      filterData?.FileStatus !== '' &&
      filterData?.ProducerID !== '' &&
      !permissions.isIMO
    ),
    retry: 2,
    timeout: 300,
  });

  const getFilesUploadedByProducer = useQuery({
    queryKey: ['GetFilesUploadedByProducer', filterData?.ProducerID, filterData?.FileStatus],
    queryFn: GetFilesUploadedByProducer(filterData),
    enabled: !!(user !== null && filterData?.ProducerID !== '' && filterData?.FileStatus !== ''),
    retry: 2,
    timeout: 300,
  });

  function refreshUploadedData() {
    if (permissions.isAdmin) {
      getFilesUploadedByAgencyAndStatus.refetch();
    } else {
      getFilesUploadedByProducer.refetch();
    }
  }

  if (user.isHO === true) {
    return (
      <Box sx={{ paddingBottom: '5rem' }}>
        <StrikeThroughHeader>Secure File Uploader</StrikeThroughHeader>
        <Typography color="text.secondary" sx={{ padding: '1rem 0' }}>
          The Secure File Uploader is not available for Home Office users.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ paddingBottom: '5rem' }}>
      <StrikeThroughHeader>Secure File Uploader</StrikeThroughHeader>
      <Stack
        padding={'1rem 0'}
        direction={'row'}
        gap={2}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Stack direction={'row'} gap={1}>
          {permissions.isAdmin && (
            <>
              <QuerySelect
                queryKey={['GetFilterOptions_DropDown']}
                queryFn={GetFilterOptions_DropDown()}
                label="Filter"
                value={filterData?.FileStatus}
                onChange={(e) =>
                  setFilterData((prevFilterData) => ({
                    ...prevFilterData,
                    FileStatus: e.target.value,
                  }))
                }
                getOptionLabel={(option) => option.text}
                getOptionValue={(option) => option.value}
                minWidth={'175px'}
              />
              <QuerySelect
                queryKey={['GetAgentOffices', filterData.ProducerID]}
                queryFn={GetAgentOffices(
                  {
                    ProducerID: filterData.ProducerID,
                    IsRSA: filterData.IsRSA,
                    IsManager: filterData.IsManager,
                  },
                  { retry: 2, timeout: 400 }
                )}
                label="Select Agency"
                value={filterData?.Agency}
                defaultToFirst={true}
                onChange={(e) =>
                  setFilterData((prevFilterData) => ({
                    ...prevFilterData,
                    Agency: e.target.value,
                  }))
                }
                getOptionLabel={(option) => option.agency}
                getOptionValue={(option) => option.agency}
                minWidth={'175px'}
                onDataLoaded={(options, rawData) => {
                  if (options.length > 0) {
                    setFilterData((prevFilterData) => ({
                      ...prevFilterData,
                      Agency: options[0].agency,
                    }));
                  }
                }}
                fallbackData={[{ agency: user?.agency }]}
              />
            </>
          )}
          <ProducerDropdown
            initialProducerID={producerID}
            defaultValue={producerID}
            setProducerID={(id) => {
              setFilterData((prevFilterData) => ({
                ...prevFilterData,
                ProducerID: id,
              }));
            }}
            formControlStyling={{ minWidth: '150px' }}
            variant="outlined"
          />
        </Stack>
        <Box>
          <Button onClick={() => setIsModalOpen(true)} variant="outlined">
            New Upload
          </Button>
        </Box>
      </Stack>
      <SecureFileUploaderDataGrid
        user={user}
        isLoading={getFilesUploadedByAgencyAndStatus.isLoading}
        isSuccess={getFilesUploadedByAgencyAndStatus.isSuccess}
        data={
          getFilesUploadedByAgencyAndStatus.data
            ? getFilesUploadedByAgencyAndStatus.data
            : getFilesUploadedByProducer.data
              ? getFilesUploadedByProducer.data
              : null
        }
        permissions={permissions}
        refreshUploadedData={refreshUploadedData}
      />
      {producerID && agentInfo && permissions && (
        <SecureFileUploaderModal
          permissions={permissions}
          toggleModal={setIsModalOpen}
          isModalOpen={isModalOpen}
          producerID={filterData.ProducerID}
          agency={filterData.Agency}
          agentInfo={agentInfo}
          refreshUploadedData={refreshUploadedData}
        />
      )}
    </Box>
  );
}

export { Index };
